import { IRegion, useGetRegionList } from "@src/hooks/newFilter/useGetLocation";
import { useGetArchitectList } from "../advertisement/service/ad.api";

import React from "react";
import ico_arrow_back from "@src/assets/ico_arrow_back.png";
import styled from "styled-components";
import { transferToNative } from "@src/lib/WebAppBridge";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const ALL_SIDO = "0000000000";

interface SelectedRegion {
    sido: IRegion;
    sigungu: IRegion;
    emd: IRegion;
}

const ArchitectSeleter = () => {
    const navigate = useNavigate();

    const [selectedSido, setSelectedSido] = useState<IRegion | null>(null);
    const [selectedSigungu, setSelectedSigungu] = useState<IRegion | null>(null);
    const [selectedEmds, setSelectedEmds] = useState<SelectedRegion[]>([]);
    const [focusedEmd, setFocusedEmd] = useState<IRegion | null>(null);
    const [pendingEmd, setPendingEmd] = useState<SelectedRegion | null>(null);

    const { data: sidoList } = useGetRegionList(ALL_SIDO);
    const { data: sigunguList } = useGetRegionList(selectedSido?.cortarNo ?? "");
    const { data: emdList } = useGetRegionList(selectedSigungu?.cortarNo ?? "");
    const { data: architectListRes, isLoading: isArchitectLoading } = useGetArchitectList(
        { pnu: `${focusedEmd?.cortarNo}000000000` },
        !!focusedEmd,
    );

    const hasArchitectAd = !!(architectListRes && architectListRes.list && architectListRes.list.length > 0);

    useEffect(() => {
        if (!pendingEmd || isArchitectLoading) return;
        if (focusedEmd?.cortarNo !== pendingEmd.emd.cortarNo) return;

        if (!hasArchitectAd) {
            setSelectedEmds((prev) => [pendingEmd, ...prev]);
            setSelectedSido(null);
            setSelectedSigungu(null);
        }
        setPendingEmd(null);
    }, [pendingEmd, isArchitectLoading, hasArchitectAd, focusedEmd]);

    const handleSelectSido = (region: IRegion) => {
        setSelectedSido(region);
        setSelectedSigungu(null);
        setFocusedEmd(null);
        setPendingEmd(null);
    };

    const handleSelectSigungu = (region: IRegion) => {
        setSelectedSigungu(region);
        setFocusedEmd(null);
        setPendingEmd(null);
    };

    const handleSelectEmd = (region: IRegion) => {
        const exists = selectedEmds.some((r) => r.emd.cortarNo === region.cortarNo);
        if (exists) {
            setSelectedEmds((prev) => prev.filter((r) => r.emd.cortarNo !== region.cortarNo));
            return;
        }
        if (selectedEmds.length >= 10) return;
        setFocusedEmd(region);
        setPendingEmd({ sido: selectedSido!, sigungu: selectedSigungu!, emd: region });
    };

    const handleRemoveEmd = (cortarNo: string) => {
        setSelectedEmds((prev) => prev.filter((r) => r.emd.cortarNo !== cortarNo));
    };

    const isComplete = selectedEmds.length > 0;

    const handleComplete = () => {
        if (!isComplete) return;
        const result = JSON.stringify(
            selectedEmds.map((item) => ({
                cortarNo: item.emd.cortarNo,
                sido: item.sido,
                sigungu: item.sigungu,
                emd: item.emd,
            })),
        );
        transferToNative(result, "selectADLocalRegion");
    };

    return (
        <Layout>
            <Header>
                <div className="prevBtn" onClick={() => navigate("/advertisement-architect/info")}>
                    <img src={ico_arrow_back} alt="뒤로가기" />
                </div>
                <div className="content">
                    <span>건축사 광고 신청</span>
                </div>
            </Header>

            <TabRow>
                <TabItem $active={true}>시도</TabItem>
                <TabItem $active={!!selectedSido}>시군구</TabItem>
                <TabItem $active={!!selectedSigungu}>읍면동</TabItem>
            </TabRow>

            <ColumnContainer>
                <Column>
                    <ColumnList>
                        {sidoList?.map((region) => (
                            <RegionItem
                                key={region.cortarNo}
                                $selected={selectedSido?.cortarNo === region.cortarNo}
                                onClick={() => handleSelectSido(region)}
                            >
                                {region.cortarName}
                            </RegionItem>
                        ))}
                    </ColumnList>
                </Column>

                <Column>
                    <ColumnList>
                        {selectedSido &&
                            sigunguList?.map((region) => (
                                <RegionItem
                                    key={region.cortarNo}
                                    $selected={selectedSigungu?.cortarNo === region.cortarNo}
                                    onClick={() => handleSelectSigungu(region)}
                                >
                                    {region.cortarName}
                                </RegionItem>
                            ))}
                    </ColumnList>
                </Column>

                <Column>
                    <ColumnList>
                        {selectedSigungu &&
                            emdList?.map((region) => (
                                <RegionItem
                                    key={region.cortarNo}
                                    $selected={selectedEmds.some((r) => r.emd.cortarNo === region.cortarNo)}
                                    onClick={() => handleSelectEmd(region)}
                                >
                                    {region.cortarName}
                                </RegionItem>
                            ))}
                    </ColumnList>
                </Column>
            </ColumnContainer>

            <BottomArea>
                <div>
                    {selectedEmds.length > 0 && (
                        <SelectedEmdsContainer>
                            {selectedEmds.map((item) => (
                                <SelectedEmdChip key={item.emd.cortarNo}>
                                    <span>
                                        {item.sido.cortarName} &gt; {item.sigungu.cortarName} &gt; {item.emd.cortarName}
                                    </span>
                                    <RemoveButton onClick={() => handleRemoveEmd(item.emd.cortarNo)}>×</RemoveButton>
                                </SelectedEmdChip>
                            ))}
                        </SelectedEmdsContainer>
                    )}

                    {focusedEmd && hasArchitectAd && (
                        <InfoLayout>
                            <InfoBoxUsed>
                                <InfoText>
                                    <div className="Local">
                                        <strong>
                                            {'"'}
                                            {focusedEmd.cortarName}
                                            {'" '}
                                        </strong>
                                        지역은 이미 지역광고가 진행중입니다.
                                    </div>
                                </InfoText>
                            </InfoBoxUsed>
                        </InfoLayout>
                    )}
                </div>

                <div>
                    <MaxLimitText>최대 10개까지 선택 가능해요!</MaxLimitText>
                    <CompleteButton $active={!!isComplete} disabled={!isComplete} onClick={handleComplete}>
                        {isArchitectLoading ? "확인 중..." : "완료"}
                    </CompleteButton>
                </div>
            </BottomArea>
        </Layout>
    );
};

export default ArchitectSeleter;

const Layout = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    height: calc(var(--vh, 1vh) * 100);
    background: #ffffff;
`;

const Header = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
    min-height: 56px;
    height: 56px;
    padding: 14px;

    .prevBtn {
        position: absolute;
        left: 5px;
        top: 10px;
        width: 40px;
        height: 40px;
        padding: 10px;
        cursor: pointer;

        & > img {
            width: 10px;
            height: 17px;
        }
    }

    .content {
        display: flex;
        align-items: center;
        font-size: 18px;
        font-weight: 700;
        line-height: 28px;
        color: #0c002c;
    }
`;

const TabRow = styled.div`
    display: flex;
    border-bottom: 1px solid #e3e3e6;
`;

const TabItem = styled.div<{ $active: boolean }>`
    flex: 1;
    padding: 12px 0;

    background-color: #f0f0f0;
    border-right: 0.6px solid #c9c9c9;

    color: #0c002c;
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
`;

const ColumnContainer = styled.div`
    display: flex;
    overflow: hidden;
    min-height: 50vh;
    max-height: 50vh;
    height: 50vh;
`;

const Column = styled.div`
    flex: 1;
    border-right: 1px solid #f0f0f0;
    overflow: hidden;

    &:last-child {
        border-right: none;
    }
`;

const ColumnList = styled.div`
    height: 100%;
    overflow-y: auto;
    padding: 14px 18px;

    display: flex;
    flex-direction: column;
    gap: 8px;

    /* -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    } */
`;

const RegionItem = styled.div<{ $selected: boolean }>`
    padding: 8px 14px;
    cursor: pointer;

    border-radius: 4px;
    background: ${({ $selected }) => ($selected ? "#2275EE" : "#F0F0F0")};

    color: ${({ $selected }) => ($selected ? "#FFF" : "#0C002C")};
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;

    &:active {
        background: #f6faff;
    }
`;

const BottomArea = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    padding: 12px 14px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
    background: #ffffff;
    border-top: 1px solid #e3e3e6;
`;

const SelectedEmdsContainer = styled.div`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const SelectedEmdChip = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border-radius: 24px;
    border: 1.2px solid #0c002c;
    background: #ffffff;
    color: #0c002c;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    flex-shrink: 0;
`;

const RemoveButton = styled.span`
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    color: #0c002c;
`;

const InfoLayout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 12px;
`;

const InfoBox = styled.div`
    position: relative;
    width: fit-content;
    padding: 6px;

    border-radius: 4px;
    background: #e9f1fd;

    color: #0c002c;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
`;

const InfoBoxUsed = styled(InfoBox)`
    background-color: #2275ee;

    .Local {
        color: #fff !important;

        strong {
            color: #fff !important;
        }
    }
`;

const InfoText = styled.div`
    width: fit-content;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 4px;

    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;

    .Local {
        color: #0c002c;

        strong {
            color: #2275ee;
        }
    }
`;

const MaxLimitText = styled.div`
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #ff4444;
    margin-bottom: 8px;
`;

const CompleteButton = styled.button<{ $active: boolean }>`
    width: 100%;
    height: 52px;
    border-radius: 12px;
    border: none;
    background: ${({ $active }) => ($active ? "#2275EE" : "#D4D4D8")};
    cursor: ${({ $active }) => ($active ? "pointer" : "default")};
    transition: all 0.15s ease;
    color: #ffffff;
    text-align: center;
    font-size: 18px;
    font-weight: 400;
    line-height: 28px;
`;
